import { css } from 'lit';
import { MapLayerGroups } from '@/config/ConfigCARSx';
import { GeoJsonProperties } from 'geojson';
import { TemplateResult, nothing } from 'lit';
import { DirectiveResult } from 'lit/directive';
import { UnsafeHTMLDirective } from 'lit/directives/unsafe-html.js';

export type DeepRequired<T> = {
	[K in keyof T]: Required<DeepRequired<T[K]>>;
};

export type LatLngBounds = {
	NorthLat: number;
	EastLon: number;
	SouthLat: number;
	WestLon: number;
};

export type LatLng = {
	lat: number;
	lng: number;
};

export type LatLon = {
	lat: number;
	lon: number;
};

export enum NotificationErrorType {
	ERROR = 'ERROR',
	SUCCESS = 'SUCCESS',
	WARNING = 'WARNING',
}

export enum BannerLocation {
	MAIN = 'MAIN',
	MODAL = 'MODAL',
}

export type Banner = {
	id: number;
	type: NotificationErrorType;
	messaging: { title: string; messages: string[] };
	timeout: number;
	location: BannerLocation;
};

export type TimelineDayEntryChange = {
	subject: string;
	description: string;
	highlighted?: boolean;
	renderer?: TemplateResult;
};

export type TimelineDayEntry = {
	timeString: string | TemplateResult;
	user: string;
	changes: TimelineDayEntryChange[];
};

export type TimelineDay = {
	dateString: string; //	"MM/DD/YYYY"
	entries: TimelineDayEntry[];
};

/*	REM activity timeline. HH and Metrics still use the TimelineDay shapes above.	*/

export type TimelineActivityCategory =
	| 'event'
	| 'publish'
	| 'signs'
	| 'attributes'
	| 'units'
	| 'dispositions'
	| 'notes'
	| 'vehicles'
	| 'links'
	| 'email';

/**	Fields that need a dedicated widget rather than plain text.	*/
export type TimelineFieldRenderer = 'publish-status';

/**	A single changed field, kept as previous/current so the UI can strike the old value through.	*/
export type TimelineActivityField = {
	label: string;
	current?: string;
	previous?: string;
	cleared?: boolean;
	highlighted?: boolean;
	renderAs?: TimelineFieldRenderer;
};

export type TimelineActivitySign = {
	name: string;
	displayed?: boolean; //	message went up on the board, not just the queue
	removed?: boolean;
};

export type TimelineActivity = {
	key: string;
	category: TimelineActivityCategory;
	groupNames: string[]; //	originating field group names, used to filter the Sign Updates view
	title: string;
	subtitle?: string;
	user: string;
	timestamp: number;
	fields: TimelineActivityField[];
	signs?: TimelineActivitySign[];
	messagePages?: string[];
	attributeChanges?: { action: 'Added' | 'Removed'; names: string[] }[];
	expandable: boolean;
	searchText: string; //	pre-lowercased haystack for the search box
};

export type TimelineMoment = {
	key: string;
	timestamp: number;
	/**	Localised time of day including the timezone, e.g. "02:44 PM PDT".	*/
	timeString: string;
	users: string[];
	activities: TimelineActivity[];
};

export type TimelineActivityDay = {
	key: string;
	dateString: string;
	timestamp: number;
	activityCount: number;
	moments: TimelineMoment[];
};

export type GeoSearchResult = {
	label: string;
	lat: number;
	lng: number;
	icon?: HTMLElement;
	extraTest?: (str: string) => boolean;
	id?: number;
	mapLayerGroup?: MapLayerGroups;
	properties?: GeoJsonProperties;
	resultType?: string;
};

export enum MonitorGroup {
	MY_MONITORS = 'MY_MONITORS',
	ALL_MONITORS = 'ALL_MONITORS',
}

export type APIErrorJSON = {
	timestamp: string;
	status: number;
	error: string;
	message: string;
	path: string;
};

export type MinMaxTuple = [number, number];

export type CartesianCoordinates = {
	x: number;
	y: number;
};

export type RenderResult =
	| TemplateResult
	| DirectiveResult<typeof UnsafeHTMLDirective>
	| string
	| number
	| string[]
	| number[]
	| undefined
	| typeof nothing;

export enum StartEnd {
	START = 'start',
	END = 'end',
}

type RGB = `rgb(${number}, ${number}, ${number})`;
type RGBA = `rgba(${number}, ${number}, ${number}, ${number})`;
type HEX = `#${string}`;

export type Color = RGB | RGBA | HEX;

export interface CreateDetourLayerOptions {
	id: string;
	symbolLayerID: string;
	source: string;
	color?: string;
	arrowSpacing?: number;
	oneWay?: boolean;
}

/**
 * Helper type when you need more specific typing than [string, T[K]] when calling Object.entries
 * @see {@link https://stackoverflow.com/a/60142095}
 */
export type Entries<T> = {
	[K in keyof T]: [K, T[K]];
}[keyof T][];

type DotNotationDepth = [never, 0, 1, 2, 3, 4];

export type DotNotationPath<T, D extends number = 4, K extends keyof T = keyof T> = D extends 0
	? never
	: K extends string
		? NonNullable<T[K]> extends ReadonlyArray<infer U>
			?
					| K
					| `${K}[${number}]`
					| (U extends Record<string, unknown>
							? `${K}[${number}].${DotNotationPath<U, DotNotationDepth[D]>}`
							: never)
			: NonNullable<T[K]> extends Record<string, unknown>
				? K | `${K}.${DotNotationPath<NonNullable<T[K]>, DotNotationDepth[D]>}`
				: K
		: never;

export interface AppAlert {
	title: string;
	category: 'ERROR' | 'CRITICAL' | 'WARNING';
	source: string;
	icon: string;
	content: string;
	timeInMillis: number;
	signId?: number;
	actions: Array<{ actionTitle: string; callbackFn?: () => void; link?: string; icon?: string }>;
}
